/**
 * salesRoutes.js
 *
 * THE single route vocabulary of the mobile Sales module (app mode `sales`).
 *
 * The Sales module is split across several screens that are built and registered
 * independently — the mode home and its four tabs, the scanner-first new-sale
 * flow, the lead/customer lists it reuses from the management layer. Spelling a
 * route name as a string literal in each of them is how a navigator ends up with
 * a `SalesOrdersTab` on one side and a `SalesOrders` on the other, which fails at
 * runtime with the deeply unhelpful "The action 'NAVIGATE' … was not handled".
 * Every Sales screen therefore imports its target names from here.
 *
 * Like {@link module:config/leadStatuses} this module is pure metadata with NO
 * react-native / react-navigation / i18n import, so it stays Jest-safe and can be
 * required from a plain unit test. {@link navigateToSalesRoute} only ever touches
 * the navigation object it is handed.
 *
 * @module config/salesRoutes
 */

/**
 * Stack route of the Sales mode home, i.e. the screen `getRouteForAppMode('sales')`
 * resolves to. MUST stay identical to `MODULE_HOME_ROUTES.sales` in
 * {@link module:config/appVariantManifest} — a mismatch silently lands a sales
 * user on the POS home.
 * @type {string}
 */
export const SALES_HOME_ROUTE = 'SalesHomeScreen';

/**
 * Bottom-tab route names of {@link module:screens/SalesHomeScreen}.
 *
 * Tab route names live in their own namespace (they are children of the Sales
 * tab navigator, not of the root stack), but they are still deliberately
 * prefixed so a `navigation.navigate(...)` that walks up to the root stack can
 * never collide with a stack screen of the same name.
 *
 * @type {{dashboard: string, leads: string, customers: string, orders: string, more: string}}
 */
export const SALES_TABS = {
    dashboard: 'SalesDashboardTab',
    leads: 'SalesLeadsTab',
    customers: 'SalesCustomersTab',
    orders: 'SalesOrdersTab',
    // The account / tooling / settings hub. A TAB, not a stack route: the Sales
    // mode has no other header affordance, so the only way to guarantee the hub
    // is reachable from every tab is to put it in the bar itself.
    more: 'SalesMoreTab',
};

/**
 * Ordered tab route names — the exact order the bottom bar renders them in, and
 * the allow-list {@link module:helpers/useLastTab} validates remembered tabs
 * against.
 * @type {string[]}
 */
export const SALES_TAB_NAMES = [
    SALES_TABS.dashboard,
    SALES_TABS.leads,
    SALES_TABS.customers,
    SALES_TABS.orders,
    SALES_TABS.more,
];

/**
 * `useLastTab` storage key of the Sales tab navigator. Per-navigator, so
 * switching between app modes never clobbers another mode's remembered tab.
 * @type {string}
 */
export const SALES_NAV_KEY = 'sales';

/**
 * Stack route of the scanner-first new-sale flow (the cart builder). Kept here
 * rather than inline so the cockpit's "New sale" action and the screen's own
 * registration agree by construction. Every param it accepts is optional, so it
 * is safe to open with none.
 * @type {string}
 */
export const SALES_NEW_ORDER_ROUTE = 'SalesOrderBuilderScreen';

/**
 * Stack route of the order review/confirm step the builder hands off to.
 * @type {string}
 */
export const SALES_ORDER_REVIEW_ROUTE = 'SalesOrderReviewScreen';

/**
 * Stack route of the Sales-native customer 360 (`{customerId, customer?, customerType?}`).
 *
 * A Sales customer row prefers this over {@link MANAGEMENT_DETAIL_ROUTE}: it adds
 * the rep-facing order history / timeline / note composer. Callers should fall
 * back to the generic management detail when a build does not register it — see
 * {@link navigateToSalesRoute}, which reports that case instead of throwing.
 * @type {string}
 */
export const SALES_CUSTOMER_DETAIL_ROUTE = 'SalesCustomerDetailScreen';

/**
 * Stack route of the "Teendők" (sales follow-ups) screen. Registered in the shared management block, so it is
 * reachable from both the Sales and the Management app mode. Optional param: `focus` (followUpId).
 * @type {string}
 */
export const SALES_FOLLOW_UPS_ROUTE = 'SalesFollowUpsScreen';

/**
 * Stack route of the Apollo.io prospecting screen — company / decision-maker
 * search, contact reveal and one-tap import of the result as a CRM lead.
 *
 * NOT a Sales-owned screen: it lives in `screens/apollo/` and is registered in
 * the SHARED management block of `components/AppNavigator.jsx`, because the
 * Management app mode reaches the very same route from the CRM hub. It is named
 * here anyway for the same reason every other route in this file is — the Sales
 * "Egyéb" hub is one of its entry points, and a route name spelled as a string
 * literal in one screen and a constant in another is exactly how the two drift
 * apart into an unhandled `NAVIGATE`.
 *
 * Needs no special handling in {@link navigateToSalesRoute}: that helper is
 * route-name agnostic and climbs `getParent()` until it finds the navigator that
 * declares the name, so a root-stack route resolves from a Sales TAB screen just
 * like {@link MANAGEMENT_LIST_ROUTE} and {@link ORDER_DETAILS_ROUTE} already do.
 * A build that ships without the Apollo module simply reports `false` and the
 * caller shows its localized "unavailable" notice instead of throwing.
 * @type {string}
 */
export const SALES_APOLLO_ROUTE = 'ApolloSearchScreen';

/**
 * Stack routes of the LinkedIn module (inbox, people / company search, connection). Registered in the same
 * shared block as the Apollo screens, so they resolve from the Sales and the Management app mode alike.
 * @type {string}
 */
export const SALES_LINKEDIN_INBOX_ROUTE = 'LinkedinInboxScreen';
export const SALES_LINKEDIN_SEARCH_ROUTE = 'LinkedinSearchScreen';
export const SALES_LINKEDIN_POSTS_ROUTE = 'LinkedinPostsScreen';
export const SALES_LINKEDIN_NETWORK_ROUTE = 'LinkedinNetworkScreen';
export const SALES_LINKEDIN_CAMPAIGNS_ROUTE = 'LinkedinCampaignsScreen';
export const SALES_LINKEDIN_CAMPAIGN_EDITOR_ROUTE = 'LinkedinCampaignEditorScreen';
export const SALES_LINKEDIN_CAMPAIGN_DETAIL_ROUTE = 'LinkedinCampaignDetailScreen';
export const SALES_LINKEDIN_ANALYTICS_ROUTE = 'LinkedinAnalyticsScreen';
export const SALES_LINKEDIN_POST_COMMENTS_ROUTE = 'LinkedinPostCommentsScreen';

/**
 * Stack route of the Apollo.io integration settings.
 *
 * SCOPE, precisely: the store's Apollo credentials — the enable toggle, the
 * write-only API key and the base URL. Nothing else lives here. The wallet, the
 * credit packages and the top-up flow are NOT on this screen; they are a sheet
 * ({@link module:components/apollo/ApolloTopupModal}) rendered by the search
 * screen itself, so an out-of-credits prompt never unmounts a half-finished
 * search. Sending a user here to buy credits would dead-end them on a
 * credentials form, which is exactly what it used to do.
 *
 * Reached from the search screen's header; named here so the Sales mode can link
 * it directly if a later surface needs to.
 * @type {string}
 */
export const SALES_APOLLO_SETTINGS_ROUTE = 'ApolloSettingsScreen';

/**
 * Stack route of the shared, config-driven management list the Sales module
 * reuses for its Leads and Customers tabs (`type` + `hideModuleBar` are passed as
 * params).
 * @type {string}
 */
export const MANAGEMENT_LIST_ROUTE = 'ManagementListScreen';

/**
 * Stack route of the shared, config-driven management record detail. Sales lead
 * and customer rows open it with `{type, item}` — exactly what
 * `ManagementListScreen` itself dispatches — so a record looks the same however
 * the user reached it.
 * @type {string}
 */
export const MANAGEMENT_DETAIL_ROUTE = 'ManagementDetailScreen';

/**
 * Stack route of the shared order-details screen. A Sales order row opens the
 * SAME detail screen every other module uses (`{orderData, orderId}` params) —
 * the Sales module deliberately has no detail screen of its own.
 * @type {string}
 */
export const ORDER_DETAILS_ROUTE = 'OrderDetailsScreen';

/**
 * Segment ids of {@link module:screens/sales/SalesOrdersScreen}. Passed as the
 * `segment` route param to open the board directly on one of them.
 * @type {{orders: string, preOrders: string}}
 */
export const SALES_ORDER_SEGMENTS = {
    orders: 'orders',
    preOrders: 'preOrders',
};

/**
 * Walk up the navigator chain looking for the one that actually declares
 * `routeName`.
 *
 * React Navigation only exposes `routeNames` for the navigator that owns the
 * `navigation` object it handed you, so a tab screen wanting to reach a ROOT
 * STACK route has to climb through `getParent()`. Doing the climb explicitly (as
 * opposed to firing a hopeful `navigate()`) is what lets a caller tell "this
 * screen is not part of this build" apart from "the navigation failed".
 *
 * @param {object} navigation React Navigation navigation prop / `useNavigation()` result.
 * @param {string} routeName Route to look for.
 * @returns {?object} The navigator that declares the route, or `null`.
 */
export const findSalesNavigator = (navigation, routeName) => {
    if (!navigation || !routeName) {
        return null;
    }

    let current = navigation;
    // Depth guard: `getParent()` is expected to terminate at the root navigator,
    // but a broken/mocked navigation object must never spin forever here.
    let hops = 0;

    while (current && hops < 12) {
        const state = typeof current.getState === 'function' ? current.getState() : null;

        if (Array.isArray(state?.routeNames) && state.routeNames.includes(routeName)) {
            return current;
        }

        current = typeof current.getParent === 'function' ? current.getParent() : null;
        hops += 1;
    }

    return null;
};

/**
 * Navigate to a Sales route ONLY when it is registered in the running build.
 *
 * The Sales screens ship incrementally and a release can drop a whole module
 * (see {@link module:config/featureFlags}), so a cockpit action may point at a
 * route this binary does not contain. A plain `navigate()` would then throw the
 * unhandled-action error straight into the user's face; this resolves the owning
 * navigator first and reports failure to the caller, which renders a real,
 * localized message instead.
 *
 * @param {object} navigation React Navigation navigation prop / `useNavigation()` result.
 * @param {string} routeName Target route name (use a constant from this module).
 * @param {object} [params] Route params.
 * @returns {boolean} `true` when the navigation was dispatched, `false` when the
 *   route is not part of this build (nothing happened).
 */
export const navigateToSalesRoute = (navigation, routeName, params) => {
    const target = findSalesNavigator(navigation, routeName);

    if (!target || typeof target.navigate !== 'function') {
        return false;
    }

    target.navigate(routeName, params);

    return true;
};
